import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import EditorNode from './editorNode';
import EditorNodeReaderView from './editorNodeReaderView';
import { highlightMatches } from '../../utils/aaiHighlight'; // Import the helper
import './jsonEditor.css';

const removeElementById = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.remove();
  }
};

const ListItem = ({
  node,
  index,
  virtualItem,
  searchQuery, // the search query passed in from parent
  editIndex,
  view,
  handleEdit,
  handleMove,
  handleRowClick,
  handleDelete,
  droppedRow,
  onMeasured,
  nodeListRef,
  myListContext,
}) => {
  const { reorderItem, resetDroppedRow } = useContext(myListContext);

  const innerRef = useRef(null);
  const customDragElement = useRef(null);
  const [state, setState] = useState({ type: 'idle', closestEdge: null });
  const [measuredHeight, setMeasuredHeight] = useState(42);
  const [outerDivLeft, setOuterDivLeft] = useState(0); // Track the left position of the outer div

  // Create a ref callback to measure the element and update its height
  const measureRef = useCallback(
    (nodeElement) => {
      if (nodeElement) {
        const rect = nodeElement.getBoundingClientRect(); // Get bounding rect
        setOuterDivLeft(rect.left); // Capture the left position of the outer div

        const height = rect.height;
        if (height !== measuredHeight && height !== 0) {
          setMeasuredHeight(height);
          if (onMeasured) {
            onMeasured(index, height);
          }
        }
        innerRef.current = nodeElement;
      }
    },
    [index, measuredHeight, onMeasured]
  );

  const createHtmlLine = useCallback((item) => {
    const truncateText = (content, maxLength) =>
      content.length > maxLength ? `${content.substring(0, maxLength)}...` : content;

    return `
      <div class="line-container">
        <span class="line-key">${item.type}</span>
        <span class="line-emotion">${item.emotion}</span>
        <span class="line-text">${truncateText(item.content, 65)}</span>
      </div>
    `;
  }, []);

  // (Drag/Drop handlers remain unchanged)
  const handleDragStart = useCallback(
    (e) => {
      resetDroppedRow();
      removeElementById('temp-id-1');
      setState({ type: 'dragging' });
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', JSON.stringify({ node, index }));
      e.target.classList.add('dragging');

      const transparentImg = new Image();
      transparentImg.src =
        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
      e.dataTransfer.setDragImage(transparentImg, 0, 0);

      customDragElement.current = document.createElement('div');
      customDragElement.current.classList.add('custom-drag-element', 'dragging');
      customDragElement.current.id = 'temp-id-1';
      customDragElement.current.innerHTML = createHtmlLine(node);
      customDragElement.current.style.left = `${outerDivLeft}px`; // Set left to outer div's left position
      customDragElement.current.style.top = `${e.pageY}px`;
      document.body.appendChild(customDragElement.current);
    },
    [node, index, resetDroppedRow, createHtmlLine, outerDivLeft]
  );

  const handleDragEnd = useCallback(
    (e) => {
      removeElementById('temp-id-1');
      e.preventDefault();
      setState({ type: 'idle' });
      e.target.classList.remove('dragging');
      innerRef.current.style.backgroundColor = droppedRow === index ? '#a3b6e3' : 'transparent';
      customDragElement.current = null;
    },
    [droppedRow, index]
  );

  const handleDrag = useCallback(
    (e) => {
      if (customDragElement.current) {
        customDragElement.current.style.top = `${e.pageY}px`;
        customDragElement.current.style.left = `${outerDivLeft}px`;
      }
    },
    [outerDivLeft]
  );

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      if (state.type === 'dragging') return;
      innerRef.current.style.backgroundColor = '#e4e7f8';
      const middle =
        innerRef.current.getBoundingClientRect().top +
        innerRef.current.getBoundingClientRect().height / 2;
      setState({ type: 'is-over', closestEdge: e.clientY < middle ? 'top' : 'bottom' });
    },
    [state.type]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      removeElementById('temp-id-1');
      innerRef.current.style.backgroundColor = droppedRow === index ? '#a3b6e3' : 'transparent';
      customDragElement.current = null;
      setState({ type: 'idle' });

      const droppedData = JSON.parse(e.dataTransfer.getData('text/plain'));
      reorderItem({ startIndex: droppedData.index, indexOfTarget: index });
    },
    [droppedRow, index, reorderItem]
  );

  const handleDragLeave = useCallback(
    (e) => {
      innerRef.current.style.backgroundColor = droppedRow === index ? '#a3b6e3' : 'transparent';
      const scrollTop = nodeListRef.current ? nodeListRef.current.scrollTop : 0;
      const containerY =
        document.querySelector('.json-editor-container')?.getBoundingClientRect().y || 0;

      if (scrollTop === 0 && e.pageY <= containerY) {
        e.preventDefault();
        removeElementById('temp-id-1');
        customDragElement.current = null;
      }
      setState({ type: 'idle' });
    },
    [droppedRow, index, nodeListRef]
  );

  useEffect(() => {
    const element = innerRef.current;

    element.draggable = true;
    element.addEventListener('dragstart', handleDragStart);
    element.addEventListener('dragend', handleDragEnd);
    element.addEventListener('dragover', handleDragOver);
    element.addEventListener('drop', handleDrop);
    element.addEventListener('dragleave', handleDragLeave);
    element.addEventListener('drag', handleDrag);

    return () => {
      element.removeEventListener('dragstart', handleDragStart);
      element.removeEventListener('dragend', handleDragEnd);
      element.removeEventListener('dragover', handleDragOver);
      element.removeEventListener('drop', handleDrop);
      element.removeEventListener('dragleave', handleDragLeave);
      element.removeEventListener('drag', handleDrag);
    };
  }, [handleDragStart, handleDragEnd, handleDragOver, handleDrop, handleDragLeave, handleDrag]);

  const itemStyle = useMemo(
    () => ({
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: 'auto',
      boxSizing: 'border-box',
      transform: `translateY(${virtualItem.start}px)`,
      zIndex: state.type === 'is-over' ? 1 : undefined,
      borderBottom:
        state.type === 'is-over' && state.closestEdge === 'bottom'
          ? '3px solid blue'
          : '1px solid #ECECEC',
      backgroundColor: droppedRow === index ? '#a3b6e3' : 'transparent',
    }),
    [virtualItem.start, state.type, state.closestEdge, droppedRow, index]
  );

  // -------------------------
  // NEW: Create a highlighted version of the node.
  // If a search query exists, wrap matching substrings in a <mark> element.
  const highlightedNode = useMemo(() => {
    if (!searchQuery) return node;
    let hlNode = {
      ...node,
      type:
        typeof node.type === 'string'
          ? highlightMatches(node.type, searchQuery)
          : node.type,
      speaker:
        typeof node.speaker === 'string'
          ? highlightMatches(node.speaker, searchQuery)
          : node.speaker,
      emotion:
        typeof node.emotion === 'string'
          ? highlightMatches(node.emotion, searchQuery)
          : node.emotion,
      content:
        typeof node.content === 'string'
          ? highlightMatches(node.content, searchQuery)
          : node.content,
    };
    return hlNode;
  }, [node, searchQuery]);
  // -------------------------

  return (
    <div
      ref={measureRef}
      data-index={virtualItem.index}
      style={itemStyle}
      className={editIndex === index ? 'editor-node-editing' : ''}
    >
      {view === 'editor' ? (
        <EditorNode
          node={highlightedNode} // pass the highlighted version of node
          index={index}
          onEdit={handleEdit}
          onMove={handleMove}
          onRowClick={handleRowClick}
          onDelete={() => handleDelete(index)}
          isSelected={editIndex === index}
          isLast={false}
        />
      ) : (
        <EditorNodeReaderView
          node={highlightedNode} // pass the highlighted version of node
          index={index}
          onEdit={handleEdit}
          onMove={handleMove}
          onRowClick={handleRowClick}
          onDelete={() => handleDelete(index)}
          isSelected={editIndex === index}
          isLast={false}
        />
      )}
    </div>
  );
};

export default ListItem;
